import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  CircularProgress,
  InputAdornment,
  Stack,
  Alert,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import profileApi from '../../pages/Profile/profileApi';

const CreateUsername = ({ onContinue, onSkip }) => {
  const [isPublic, setIsPublic] = useState(true);
  const [username, setUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [usernameMsg, setUsernameMsg] = useState('');
  const [usernameTimeout, setUsernameTimeout] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleUsernameChange = (value) => {
    setUsername(value);
    setError('');
    if (usernameTimeout) clearTimeout(usernameTimeout);
    if (!value || value.length < 3) {
      setUsernameStatus(null);
      setUsernameMsg('');
      return;
    }
    setUsernameStatus('checking');
    const t = setTimeout(async () => {
      try {
        const res = await profileApi.checkUsername(value);
        setUsernameStatus(res.available ? 'available' : 'taken');
        setUsernameMsg(res.message);
      } catch {
        setUsernameStatus(null);
      }
    }, 600);
    setUsernameTimeout(t);
  };

  const usernameAdornment = {
    checking: <CircularProgress size={16} />,
    available: <CheckCircleIcon color="success" fontSize="small" />,
    taken: <CancelIcon color="error" fontSize="small" />,
  }[usernameStatus];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isPublic) {
      if (!username || username.length < 3) {
        setError('Choose a username (at least 3 characters), or turn off public profile to skip');
        return;
      }
      if (usernameStatus === 'taken') {
        setError('That username is already taken');
        return;
      }
    }

    setSubmitting(true);
    try {
      if (username && username.length >= 3) {
        await profileApi.setUsername(username);
      }
      await profileApi.setVisibility(isPublic);
      onContinue();
    } catch (err) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Typography variant="h6" align="center" fontWeight={700}>Set up your profile</Typography>
      <Typography variant="body2" color="text.secondary" align="center">
        Choose a username so friends can find you. You can always change this later in your profile.
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>Public Profile</Typography>
          <Typography variant="caption" color="text.secondary">
            Allow anyone to view your basic profile via a public link
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          {isPublic
            ? <Chip icon={<PublicIcon />} label="Public" color="success" size="small" />
            : <Chip icon={<LockIcon />} label="Private" size="small" />
          }
          <Switch
            checked={isPublic}
            onChange={e => setIsPublic(e.target.checked)}
            color="primary"
            disabled={submitting}
          />
        </Stack>
      </Stack>

      <TextField
        label="Username"
        value={username}
        onChange={e => handleUsernameChange(e.target.value.toLowerCase())}
        fullWidth
        placeholder="e.g. john_doe"
        helperText={usernameMsg || 'Letters, numbers, underscores and dots only'}
        error={usernameStatus === 'taken'}
        disabled={submitting}
        InputProps={{
          startAdornment: <InputAdornment position="start">@</InputAdornment>,
          endAdornment: usernameAdornment ? (
            <InputAdornment position="end">{usernameAdornment}</InputAdornment>
          ) : null,
        }}
      />

      <Button type="submit" variant="contained" size="large" fullWidth disabled={submitting}>
        {submitting ? <CircularProgress size={24} /> : 'Continue'}
      </Button>

      <Button variant="text" size="small" onClick={onSkip} disabled={submitting} sx={{ color: 'text.secondary' }}>
        Skip for now
      </Button>
    </Box>
  );
};

export default CreateUsername;
